import React, { useEffect, useState, useRef, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { FaEye, FaPrint, FaFileExcel, FaRedo, FaSort, FaSortUp, FaSortDown } from "react-icons/fa";
import {
  getAccountStatementReport,
  clearAccountStatementReport,
  clearError,
  clearSuccess,
} from "../../../../redux/Slices/AccountingReportsSlice";
import { getPostingAccounts } from "../../../../redux/Slices/AccountTreeSlice";
import { useTitle } from "../../../../shared/hooks/TitleContext";
import {
  generatePdfFromElementIds,
  waitForRender,
} from '../../../../shared/pdf/pdfEngine';
import * as XLSX from "xlsx";
import i18n from "../../../../i18n/i18n";
import {
  calculatePdfPagination,
  convertArabicNumbersToEnglish,
  extractArray,
} from "../../../../shared/utils.js/utils";
import PdfPreviewModal from "./components/PdfPreviewModal";

const extractArrayFromPayload = extractArray;

const getEntryId = (row, fallback = 0) =>
  Number(row?.id ?? row?.entry_id ?? row?.transaction_id ?? fallback);
const getEntryDate = row =>
  row?.date || row?.transaction_date || row?.created_at || "";
const getEntryDescription = row =>
  row?.description || row?.details || row?.memo || row?.notes || "";
const getEntryDebit = row => Number(row?.debit ?? row?.debits ?? 0) || 0;
const getEntryCredit = row => Number(row?.credit ?? row?.credits ?? 0) || 0;
const getEntryBalance = row =>
  Number(row?.balance ?? row?.current_balance ?? row?.running_balance ?? 0) ||
  0;

// account statement report component
const AccountStatementReport = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { setTitle } = useTitle();
  const { t } = useTranslation("global");
  const { id: accountId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const isRTL = i18n.language === "ar";
  const textAlign = isRTL ? "right" : "left";
  const dir = isRTL ? "rtl" : "ltr";
  const publicUrl = process.env.PUBLIC_URL || "";
  const headerLogo = `${publicUrl}/ACE.png`;

  const { accountStatementReport, isLoading, error, success } = useSelector(
    state => state.accountingReports,
  );
  const { postingAccounts } = useSelector(state => state.auth);

  const [selectedAccountId, setSelectedAccountId] = useState(accountId || "");
  const [fromDate, setFromDate] = useState(searchParams.get("from_date") || "");
  const [toDate, setToDate] = useState(searchParams.get("to_date") || "");
  const [accountDropdownOpen, setAccountDropdownOpen] = useState(false);
  const [accountSearchTerm, setAccountSearchTerm] = useState("");
  const accountSearchInputRef = useRef(null);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [pdfType, setPdfType] = useState(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [pdfPages, setPdfPages] = useState([]);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [sortField, setSortField] = useState("id");
  const [sortDirection, setSortDirection] = useState("asc");

  const SORT_FIELDS = { ID: 'id', DATE: 'date', DEBIT: 'debit', CREDIT: 'credit', BALANCE: 'balance' };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return <FaSort style={{ marginLeft: '4px', color: '#ccc', verticalAlign: 'middle' }} />;
    return sortDirection === 'asc'
      ? <FaSortUp style={{ marginLeft: '4px', color: '#ffd700', verticalAlign: 'middle' }} />
      : <FaSortDown style={{ marginLeft: '4px', color: '#ffd700', verticalAlign: 'middle' }} />;
  };

  useEffect(() => {
    setTitle("accountingReports.accountStatementTitle", {
      defaultValue: "كشف حساب",
    });
    dispatch(getPostingAccounts());

    // Check if page was reloaded (refresh)
    try {
      const navEntries = window.performance.getEntriesByType("navigation");
      const navType = navEntries && navEntries[0] && navEntries[0].type;

      if (navType === "reload" && accountId) {
        // On refresh, execute same logic as handleReset
        setIsRefreshing(true);
        dispatch(clearAccountStatementReport());
        setSelectedAccountId("");
        setFromDate("");
        setToDate("");
        setAccountSearchTerm("");
        setIsPreviewOpen(false);
        setSearchParams({});
        navigate("/accounts/statement", { replace: true });
        // Reset refreshing flag after navigation
        setTimeout(() => setIsRefreshing(false), 100);
        return;
      }
    } catch (e) {
      // Ignore performance API errors
    }

    // Clear data if no accountId in URL (on initial load)
    if (!accountId) {
      dispatch(clearAccountStatementReport());
      setSelectedAccountId("");
      setFromDate("");
      setToDate("");
      // Clear searchParams on refresh
      const currentParams = new URLSearchParams(window.location.search);
      if (currentParams.toString()) {
        setSearchParams({});
      }
    } else {
      setSelectedAccountId(accountId);
      // Remove query parameters from URL if they exist (but keep accountId)
      const currentParams = new URLSearchParams(window.location.search);
      if (currentParams.toString()) {
        navigate(`/accounts/${accountId}/statement`, { replace: true });
      }
    }

    return () => {
      dispatch(clearAccountStatementReport());
    };
  }, [dispatch, setTitle, accountId, setSearchParams, navigate]);

  useEffect(() => {
    if (accountId) {
      const params = {};
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      dispatch(getAccountStatementReport({ accountId, params }));
    } else {
      // Clear data when accountId is removed (e.g., after reset)
      dispatch(clearAccountStatementReport());
    }
  }, [accountId, fromDate, toDate, dispatch]);

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (accountSearchTerm) {
        dispatch(getPostingAccounts(accountSearchTerm));
      } else {
        dispatch(getPostingAccounts());
      }
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [accountSearchTerm, dispatch]);

  useEffect(() => {
    const handleClickOutside = e => {
      if (
        accountDropdownOpen &&
        !e.target.closest(".account-dropdown-container")
      ) {
        setAccountDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [accountDropdownOpen]);

  const accountList = useMemo(
    () => extractArrayFromPayload(postingAccounts),
    [postingAccounts],
  );

  const filteredAccounts = useMemo(() => {
    const sortedAccounts = [...accountList].sort(
      (a, b) => getEntryId(a) - getEntryId(b),
    );
    if (!accountSearchTerm) return sortedAccounts;
    const term = accountSearchTerm.toLowerCase();
    return sortedAccounts.filter(account => {
      const code = account.code?.toLowerCase() || "";
      const nameAr = account.name_ar?.toLowerCase() || "";
      const nameEn = account.name?.toLowerCase() || "";
      return (
        code.includes(term) || nameAr.includes(term) || nameEn.includes(term)
      );
    });
  }, [accountList, accountSearchTerm]);

  const selectedAccount = accountList.find(
    acc => String(acc.id) === String(selectedAccountId),
  );

  useEffect(() => {
    if (success) {
      toast.success(success, { rtl: isRTL });
      dispatch(clearSuccess());
    }
  }, [success, dispatch, isRTL]);

  useEffect(() => {
    if (error) {
      toast.error(error, { rtl: isRTL });
      setTimeout(() => dispatch(clearError()), 2500);
    }
  }, [error, dispatch, isRTL]);

  const handleSubmit = e => {
    e.preventDefault();
    if (!selectedAccountId) {
      toast.error(
        t("accountingReports.selectAccount", {
          defaultValue: "برجاء اختيار الحساب أولاً",
        }),
        { rtl: isRTL },
      );
      return;
    }
    const queryParams = new URLSearchParams();
    if (fromDate) queryParams.set("from_date", fromDate);
    if (toDate) queryParams.set("to_date", toDate);
    navigate(
      `/accounts/${selectedAccountId}/statement?${queryParams.toString()}`,
    );
  };

  const handleReset = () => {
    dispatch(clearAccountStatementReport());
    setSelectedAccountId("");
    setFromDate("");
    setToDate("");
    setAccountSearchTerm("");
    setIsPreviewOpen(false);
    setSearchParams({});
    navigate("/accounts/statement");
    toast.info(
      t("accountingReports.reset", { defaultValue: "تم إعادة التعيين بنجاح" }),
      { rtl: isRTL },
    );
  };

  const preparePdfPages = () => {
    const topHeader = document.getElementById(
      "account-statement-report-pdf-measure-top-header",
    );
    const thead = document.getElementById(
      "account-statement-report-pdf-measure-thead",
    );
    const footer = document.getElementById(
      "account-statement-report-pdf-measure-footer",
    );
    const tbody = document.getElementById(
      "account-statement-report-pdf-measure-tbody",
    );
    const chunks = calculatePdfPagination(
      sortedEntries,
      { topHeader, thead, footer, tbody },
      { safeMargin: 25 },
    );
    if (chunks.length === 0) {
      return [
        { pageNumber: 1, entries: [], startIndex: 0, isFirstPage: true, isLastPage: true },
      ];
    }
    return chunks.map((chunk, idx) => ({
      pageNumber: idx + 1,
      entries: chunk.items || chunk,
      startIndex: chunk.startIndex ?? idx * 10,
      isFirstPage: idx === 0,
      isLastPage: idx === chunks.length - 1,
    }));
  };

  const handlePreviewAll = () => {
    if (!accountStatementReport) {
      toast.error(
        t("accountingReports.noData", {
          defaultValue: "لا توجد بيانات للمعاينة",
        }),
        { rtl: isRTL },
      );
      return;
    }
    const pages = preparePdfPages();
    setPdfPages(pages);
    setIsPreviewOpen(true);
  };

  const handlePrintAll = async () => {
    if (!accountStatementReport) {
      toast.error(
        t("accountingReports.noData", {
          defaultValue: "لا توجد بيانات للطباعة",
        }),
        { rtl: isRTL },
      );
      return;
    }

    try {
      setPdfType("all");
      const pages = preparePdfPages();
      setPdfPages(pages);

      await new Promise(resolve => requestAnimationFrame(resolve));
      if (document.fonts && document.fonts.ready) {
        await document.fonts.ready;
      }
      await new Promise(resolve => setTimeout(resolve, 300));
      setIsGeneratingPdf(true);

      await waitForRender(300);

      // Shared PDF Engine — capture + assembly (one A4 page per chunk)
      const pageElementIds = pages.map((_, i) =>
        document.getElementById(`account-statement-report-pdf-all-${i}`)
          ? `account-statement-report-pdf-all-${i}`
          : `preview-account-statement-report-pdf-all-${i}`,
      );
      await generatePdfFromElementIds({
        elementIds: pageElementIds,
        fileName: `ACE_AccountStatement_${accountId}_${new Date().toISOString().split("T")[0]}.pdf`,
        isRTL,
      });
      toast.success(
        t("accountingReports.printed", {
          defaultValue: "تم طباعة التقرير بنجاح",
        }),
        { rtl: isRTL },
      );
    } catch (error) {
      console.error("Account Statement Report PDF error:", error);
      toast.error(
        t("accountingReports.printError", {
          defaultValue: "حدث خطأ أثناء إنشاء PDF",
        }),
        { rtl: isRTL },
      );
    } finally {
      setIsGeneratingPdf(false);
      setPdfType(null);
      setPdfPages([]);
    }
  };

  const handleExportAllToExcel = () => {
    if (!accountStatementReport) {
      toast.error(
        t("accountingReports.noData", {
          defaultValue: "لا توجد بيانات للتصدير",
        }),
        { rtl: isRTL },
      );
      return;
    }

    try {
      const workbook = XLSX.utils.book_new();

      // Details Sheet
      const detailRows = sortedEntries.map(row => ({
        [t("accountingReports.id", { defaultValue: isRTL ? "المعرف" : "ID" })]:
          getEntryId(row),
        [t("accountingReports.date", {
          defaultValue: isRTL ? "التاريخ" : "Date",
        })]: formatDate(getEntryDate(row)),
        [t("accountingReports.description", {
          defaultValue: isRTL ? "الوصف" : "Description",
        })]: getEntryDescription(row),
        [t("accountingReports.debit", {
          defaultValue: isRTL ? "مدين" : "Debit",
        })]: getEntryDebit(row),
        [t("accountingReports.credit", {
          defaultValue: isRTL ? "دائن" : "Credit",
        })]: getEntryCredit(row),
        [t("accountingReports.balance", {
          defaultValue: isRTL ? "الرصيد" : "Balance",
        })]: getEntryBalance(row),
      }));

      if (detailRows.length > 0) {
        const totalsRow = {
          [t("accountingReports.id", {
            defaultValue: isRTL ? "المعرف" : "ID",
          })]: "",
          [t("accountingReports.date", {
            defaultValue: isRTL ? "التاريخ" : "Date",
          })]: t("accountingReports.total", {
            defaultValue: isRTL ? "الإجمالي" : "Total",
          }),
          [t("accountingReports.description", {
            defaultValue: isRTL ? "الوصف" : "Description",
          })]: "",
          [t("accountingReports.debit", {
            defaultValue: isRTL ? "مدين" : "Debit",
          })]: totals.total_debit ?? totals.debit ?? 0,
          [t("accountingReports.credit", {
            defaultValue: isRTL ? "دائن" : "Credit",
          })]: totals.total_credit ?? totals.credit ?? 0,
          [t("accountingReports.balance", {
            defaultValue: isRTL ? "الرصيد" : "Balance",
          })]:
            totals.closing_balance ??
            totals.total_balance ??
            totals.balance ??
            0,
        };
        detailRows.push(totalsRow);
      }

      const detailsSheet = XLSX.utils.json_to_sheet(detailRows);
      XLSX.utils.book_append_sheet(
        workbook,
        detailsSheet,
        t("accountingReports.details", { defaultValue: "التفاصيل" }),
      );

      // Summary Sheet
      const summaryRows = [];
      if (totals?.opening_balance !== undefined) {
        summaryRows.push({
          [t("accountingReports.item", { defaultValue: "البند" })]: t(
            "accountingReports.openingBalance",
            { defaultValue: "الرصيد الافتتاحي" },
          ),
          [t("accountingReports.amount", { defaultValue: "المبلغ" })]:
            totals.opening_balance,
        });
      }
      summaryRows.push(
        {
          [t("accountingReports.item", { defaultValue: "البند" })]: t(
            "accountingReports.totalDebit",
            { defaultValue: "إجمالي المدين" },
          ),
          [t("accountingReports.amount", { defaultValue: "المبلغ" })]:
            totals.debit ?? totals.total_debit ?? 0,
        },
        {
          [t("accountingReports.item", { defaultValue: "البند" })]: t(
            "accountingReports.totalCredit",
            { defaultValue: "إجمالي الدائن" },
          ),
          [t("accountingReports.amount", { defaultValue: "المبلغ" })]:
            totals.credit ?? totals.total_credit ?? 0,
        },
      );
      if (totals?.closing_balance !== undefined) {
        summaryRows.push({
          [t("accountingReports.item", { defaultValue: "البند" })]: t(
            "accountingReports.closingBalance",
            { defaultValue: "الرصيد الختامي" },
          ),
          [t("accountingReports.amount", { defaultValue: "المبلغ" })]:
            totals.closing_balance,
        });
      }

      const summarySheet = XLSX.utils.json_to_sheet(summaryRows);
      XLSX.utils.book_append_sheet(
        workbook,
        summarySheet,
        t("accountingReports.summary", { defaultValue: "الملخص" }),
      );

      XLSX.writeFile(
        workbook,
        `AccountStatement_${accountId}_${new Date().toISOString().split("T")[0]}.xlsx`,
      );
      toast.success(
        t("accountingReports.exported", {
          defaultValue: "تم تصدير البيانات بنجاح",
        }),
        { rtl: isRTL },
      );
    } catch (error) {
      toast.error(
        error.message ||
          t("accountingReports.exportError", {
            defaultValue: "فشل في تصدير البيانات",
          }),
        { rtl: isRTL },
      );
    }
  };

  const formatNumber = value =>
    convertArabicNumbersToEnglish(
      Number(value ?? 0).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
    );

  const formatDate = dateString => {
    if (!dateString) return "—";
    return convertArabicNumbersToEnglish(
      new Date(dateString).toLocaleDateString("en-US", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      }),
    );
  };

  const reportData =
    accountStatementReport?.data || accountStatementReport || {};
  const entries = extractArray(
    reportData?.transactions ||
      reportData?.entries ||
      reportData?.movements ||
      reportData?.data ||
      reportData,
  );
  const sortedEntries = useMemo(() => {
    const getters = {
      id: getEntryId,
      date: row => new Date(getEntryDate(row)).getTime() || 0,
      debit: getEntryDebit,
      credit: getEntryCredit,
      balance: getEntryBalance,
    };
    const getter = getters[sortField] || getters.id;
    const direction = sortDirection === "desc" ? -1 : 1;

    return [...entries].sort((a, b) => {
      const first = getter(a);
      const second = getter(b);
      if (typeof first === "string" || typeof second === "string") {
        return String(first).localeCompare(String(second)) * direction;
      }
      return ((first || 0) - (second || 0)) * direction;
    });
  }, [entries, sortField, sortDirection]);
  const totals = reportData?.summary || reportData?.totals || {};
  const accountInfo = reportData?.account || {};
  const period = reportData?.period || {};
  const meta = reportData?.meta || {};
  const hasUnsupportedReportShape =
    !!accountStatementReport &&
    entries.length === 0 &&
    Object.keys(reportData || {}).length > 0 &&
    !reportData.message &&
    !reportData.summary &&
    !reportData.totals;

  return (
    <div style={{ padding: "20px", direction: isRTL ? "rtl" : "ltr" }}>
      <div
        style={{
          backgroundColor: "#1b3350",
          borderRadius: "12px",
          padding: "24px",
          border: "1px solid #333",
        }}
      >
        <h2
          style={{
            color: "white",
            marginBottom: "20px",
            direction: dir,
            textAlign,
          }}
        >
          {t("accountingReports.accountStatementTitle", {
            defaultValue: "كشف حساب",
          })}
        </h2>

        {/* Filters */}
        <form
          onSubmit={handleSubmit}
          style={{
            backgroundColor: "#1a1f2e",
            padding: "20px",
            borderRadius: "8px",
            marginBottom: "20px",
          }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
              gap: "15px",
              marginBottom: "15px",
            }}
          >
            <div
              className="account-dropdown-container"
              style={{ position: "relative" }}
            >
              <label
                style={{
                  color: "#ccc",
                  fontSize: "14px",
                  display: "block",
                  marginBottom: "8px",
                  direction: dir,
                  textAlign,
                }}
              >
                {t("accountingReports.account", { defaultValue: "الحساب" })}{" "}
                <span style={{ color: "#dc3545" }}>*</span>
              </label>
              <div
                onClick={() => {
                  setAccountDropdownOpen(!accountDropdownOpen);
                  if (!accountDropdownOpen && accountSearchInputRef.current) {
                    setTimeout(
                      () => accountSearchInputRef.current?.focus(),
                      100,
                    );
                  }
                }}
                style={{
                  width: "100%",
                  padding: "10px",
                  backgroundColor: "#14263b",
                  border: "1px solid #333",
                  borderRadius: "6px",
                  color: "white",
                  fontSize: "16px",
                  cursor: "pointer",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <span>
                  {selectedAccount
                    ? isRTL
                      ? selectedAccount.name_ar || selectedAccount.name
                      : selectedAccount.name || selectedAccount.name_ar
                    : t("accountingReports.selectAccount", {
                        defaultValue: "اختر الحساب",
                      })}
                </span>
                <span style={{ fontSize: "12px" }}>▼</span>
              </div>
              {accountDropdownOpen && (
                <div
                  style={{
                    position: "absolute",
                    top: "100%",
                    left: 0,
                    right: 0,
                    backgroundColor: "#1a1f2e",
                    border: "1px solid #333",
                    borderRadius: "6px",
                    marginTop: "4px",
                    maxHeight: "300px",
                    overflow: "auto",
                    zIndex: 1000,
                    boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
                  }}
                >
                  <input
                    ref={accountSearchInputRef}
                    type="text"
                    placeholder={t("accountingReports.search", {
                      defaultValue: "بحث...",
                    })}
                    value={accountSearchTerm}
                    onChange={e => {
                      e.stopPropagation();
                      setAccountSearchTerm(e.target.value);
                    }}
                    onClick={e => e.stopPropagation()}
                    style={{
                      width: "100%",
                      padding: "10px",
                      backgroundColor: "#14263b",
                      border: "none",
                      borderBottom: "1px solid #333",
                      color: "white",
                      fontSize: "14px",
                    }}
                  />
                  <div style={{ maxHeight: "250px", overflow: "auto" }}>
                    {filteredAccounts.length > 0 ? (
                      filteredAccounts.map(account => (
                        <div
                          key={account.id}
                          onClick={() => {
                            setSelectedAccountId(account.id.toString());
                            setAccountDropdownOpen(false);
                            setAccountSearchTerm("");
                          }}
                          style={{
                            padding: "10px",
                            cursor: "pointer",
                            color: "white",
                            borderBottom: "1px solid #333",
                            backgroundColor:
                              selectedAccountId === account.id.toString()
                                ? "#AC2000"
                                : "transparent",
                          }}
                          onMouseEnter={e => {
                            if (selectedAccountId !== account.id.toString()) {
                              e.currentTarget.style.backgroundColor = "#2a3441";
                            }
                          }}
                          onMouseLeave={e => {
                            if (selectedAccountId !== account.id.toString()) {
                              e.currentTarget.style.backgroundColor =
                                "transparent";
                            }
                          }}
                        >
                          {isRTL
                            ? account.name_ar || account.name
                            : account.name || account.name_ar}{" "}
                          {account.code ? `(${account.code})` : ""}
                        </div>
                      ))
                    ) : (
                      <div
                        style={{
                          padding: "10px",
                          color: "#999",
                          textAlign: "center",
                        }}
                      >
                        {t("accountingReports.noAccounts", {
                          defaultValue: "لا توجد حسابات",
                        })}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
            <div>
              <label
                style={{
                  color: "#ccc",
                  fontSize: "14px",
                  display: "block",
                  marginBottom: "8px",
                  direction: dir,
                  textAlign,
                }}
              >
                {t("accountingReports.fromDate", { defaultValue: "من تاريخ" })}
              </label>
              <input
                type="date"
                value={fromDate}
                onChange={e => setFromDate(e.target.value)}
                style={{
                  width: "100%",
                  padding: "10px",
                  backgroundColor: "#14263b",
                  border: "1px solid #333",
                  borderRadius: "6px",
                  color: "white",
                  fontSize: "16px",
                }}
              />
            </div>
            <div>
              <label
                style={{
                  color: "#ccc",
                  fontSize: "14px",
                  display: "block",
                  marginBottom: "8px",
                  direction: dir,
                  textAlign,
                }}
              >
                {t("accountingReports.toDate", { defaultValue: "إلى تاريخ" })}
              </label>
              <input
                type="date"
                value={toDate}
                onChange={e => setToDate(e.target.value)}
                style={{
                  width: "100%",
                  padding: "10px",
                  backgroundColor: "#14263b",
                  border: "1px solid #333",
                  borderRadius: "6px",
                  color: "white",
                  fontSize: "16px",
                }}
              />
            </div>
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              gap: "10px",
              flexWrap: "wrap",
            }}
          >
            <button
              type="submit"
              disabled={isLoading}
              style={{
                backgroundColor: isLoading ? "#666" : "#AC2000",
                color: "white",
                border: "none",
                padding: "12px 24px",
                borderRadius: "8px",
                cursor: isLoading ? "not-allowed" : "pointer",
                fontSize: "16px",
                opacity: isLoading ? 0.6 : 1,
                minWidth: "160px",
              }}
            >
              {isLoading
                ? t("accountingReports.loading", {
                    defaultValue: "جاري التحميل...",
                  })
                : t("accountingReports.view", { defaultValue: "عرض" })}
            </button>
            <button
              type="button"
              onClick={handleReset}
              style={{
                backgroundColor: "#6c757d",
                color: "white",
                border: "none",
                padding: "12px 24px",
                borderRadius: "8px",
                cursor: "pointer",
                fontSize: "16px",
                minWidth: "160px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
              }}
            >
              <FaRedo />{" "}
              {t("accountingReports.reset", { defaultValue: "إعادة تعيين" })}
            </button>
          </div>
        </form>

        {error && (
          <div
            style={{
              backgroundColor: "#3b1f24",
              border: "1px solid #dc3545",
              color: "#ffd8de",
              padding: "12px 14px",
              borderRadius: "8px",
              marginBottom: "20px",
              direction: dir,
              textAlign,
            }}
          >
            <strong>
              {t("accountingReports.loadErrorTitle", {
                defaultValue: isRTL
                  ? "تعذر تحميل كشف الحساب"
                  : "Unable to load account statement",
              })}
            </strong>
            <div style={{ marginTop: "6px", fontSize: "14px" }}>
              {error ||
                t("accountingReports.loadErrorBody", {
                  defaultValue: isRTL
                    ? "حدث خطأ أثناء جلب البيانات. راجع الاتصال أو الحساب المختار ثم حاول مرة أخرى."
                    : "An error occurred while fetching the data. Check the connection or selected account and try again.",
                })}
            </div>
          </div>
        )}

        {hasUnsupportedReportShape && (
          <div
            style={{
              backgroundColor: "#3b321f",
              border: "1px solid #ffc107",
              color: "#ffe9a8",
              padding: "12px 14px",
              borderRadius: "8px",
              marginBottom: "20px",
              direction: dir,
              textAlign,
            }}
          >
            <strong>
              {t("accountingReports.dataShapeWarningTitle", {
                defaultValue: isRTL
                  ? "تم تحميل الرد لكن شكل البيانات غير متوقع"
                  : "The response loaded, but the data format is unexpected",
              })}
            </strong>
            <div style={{ marginTop: "6px", fontSize: "14px" }}>
              {t("accountingReports.dataShapeWarningBody", {
                defaultValue: isRTL
                  ? "لم يتم العثور على قائمة حركات قابلة للعرض داخل الرد. راجع بيانات الـ API أو جرّب حساباً آخر."
                  : "No displayable movement list was found in the response. Check the API data or try another account.",
              })}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        {accountStatementReport && !isRefreshing && (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              gap: "10px",
              marginBottom: "20px",
              flexWrap: "wrap",
            }}
          >
            <button
              onClick={handlePreviewAll}
              style={{
                backgroundColor: "#444",
                color: "white",
                border: "none",
                padding: "12px 20px",
                borderRadius: 8,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 8,
                justifyContent: "center",
              }}
            >
              <FaEye />{" "}
              {t("accountingReports.previewAllPDF", {
                defaultValue: "عرض PDF للكل",
              })}
            </button>
            <button
              onClick={handlePrintAll}
              disabled={isGeneratingPdf}
              style={{
                backgroundColor: isGeneratingPdf ? "#666" : "#17a2b8",
                color: "white",
                border: "none",
                padding: "12px 20px",
                borderRadius: 8,
                cursor: isGeneratingPdf ? "not-allowed" : "pointer",
                display: "flex",
                alignItems: "center",
                gap: 8,
                justifyContent: "center",
                opacity: isGeneratingPdf ? 0.6 : 1,
              }}
            >
              <FaPrint />{" "}
              {isGeneratingPdf
                ? t("accountingReports.generating", {
                    defaultValue: "جاري التوليد...",
                  })
                : t("accountingReports.printAllPDF", {
                    defaultValue: "طباعة PDF للكل",
                  })}
            </button>
            <button
              onClick={handleExportAllToExcel}
              style={{
                backgroundColor: "#28a745",
                color: "white",
                border: "none",
                padding: "12px 20px",
                borderRadius: 8,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 8,
                justifyContent: "center",
              }}
            >
              <FaFileExcel />{" "}
              {t("accountingReports.exportExcel", {
                defaultValue: "تصدير Excel للكل",
              })}
            </button>
          </div>
        )}

        {/* Report Data Display */}
        {accountStatementReport && !isRefreshing && (
          <div
            style={{
              backgroundColor: "#1a1f2e",
              padding: "20px",
              borderRadius: "8px",
              marginTop: "20px",
            }}
          >
            <div style={{ marginBottom: "20px" }}>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                  gap: "15px",
                  marginBottom: "15px",
                }}
              >
                <div
                  style={{
                    backgroundColor: "#252f3f",
                    padding: "15px",
                    borderRadius: "8px",
                    border: "1px solid #333",
                    direction: dir,
                  }}
                >
                  <div
                    style={{
                      color: "#999",
                      fontSize: "12px",
                      marginBottom: "5px",
                      textAlign,
                    }}
                  >
                    {t("accountingReports.account", { defaultValue: "الحساب" })}
                  </div>
                  <div
                    style={{
                      color: "#fff",
                      fontSize: "16px",
                      fontWeight: "bold",
                      textAlign,
                    }}
                  >
                    {accountInfo
                      ? isRTL
                        ? accountInfo.name_ar ||
                          accountInfo.name_en ||
                          accountInfo.name
                        : accountInfo.name_en ||
                          accountInfo.name_ar ||
                          accountInfo.name
                      : `#${accountId}`}
                  </div>
                  {accountInfo?.code && (
                    <div
                      style={{
                        color: "#999",
                        fontSize: "12px",
                        marginTop: "5px",
                        textAlign,
                      }}
                    >
                      {accountInfo.code}
                    </div>
                  )}
                </div>
                <div
                  style={{
                    backgroundColor: "#252f3f",
                    padding: "15px",
                    borderRadius: "8px",
                    border: "1px solid #333",
                    direction: dir,
                  }}
                >
                  <div
                    style={{
                      color: "#999",
                      fontSize: "12px",
                      marginBottom: "5px",
                      textAlign,
                    }}
                  >
                    {t("accountingReports.period", { defaultValue: "الفترة" })}
                  </div>
                  <div
                    style={{
                      color: "#fff",
                      fontSize: "16px",
                      fontWeight: "bold",
                      textAlign,
                    }}
                  >
                    {period?.from && period?.to
                      ? `${formatDate(period.from)} - ${formatDate(period.to)}`
                      : `${formatDate(fromDate)} - ${formatDate(toDate)}`}
                  </div>
                </div>
                {totals?.opening_balance !== undefined && (
                  <div
                    style={{
                      backgroundColor: "#1e3a5f",
                      padding: "15px",
                      borderRadius: "8px",
                      border: "1px solid #2a4a6f",
                      direction: dir,
                    }}
                  >
                    <div
                      style={{
                        color: "#a0c4ff",
                        fontSize: "12px",
                        marginBottom: "5px",
                        textAlign,
                      }}
                    >
                      {t("accountingReports.openingBalance", {
                        defaultValue: "الرصيد الافتتاحي",
                      })}
                    </div>
                    <div
                      style={{
                        color: "#fff",
                        fontSize: "18px",
                        fontWeight: "bold",
                        textAlign,
                      }}
                    >
                      {formatNumber(totals.opening_balance)}
                    </div>
                  </div>
                )}
                {totals?.closing_balance !== undefined && (
                  <div
                    style={{
                      backgroundColor: "#1e5f3a",
                      padding: "15px",
                      borderRadius: "8px",
                      border: "1px solid #2a6f4a",
                      direction: dir,
                    }}
                  >
                    <div
                      style={{
                        color: "#a0ffc4",
                        fontSize: "12px",
                        marginBottom: "5px",
                        textAlign,
                      }}
                    >
                      {t("accountingReports.closingBalance", {
                        defaultValue: "الرصيد الختامي",
                      })}
                    </div>
                    <div
                      style={{
                        color: "#fff",
                        fontSize: "18px",
                        fontWeight: "bold",
                        textAlign,
                      }}
                    >
                      {formatNumber(totals.closing_balance)}
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: isRTL ? "flex-start" : "flex-end",
                gap: "12px",
                flexWrap: "wrap",
                marginBottom: "12px",
                direction: dir,
              }}
            >
              <label
                style={{
                  color: "#ccc",
                  fontSize: "13px",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                {t("accountingReports.sortBy", {
                  defaultValue: isRTL ? "الترتيب حسب" : "Sort by",
                })}
                <select
                  value={sortField}
                  onChange={e => setSortField(e.target.value)}
                  style={{
                    backgroundColor: "#14263b",
                    color: "white",
                    border: "1px solid #333",
                    borderRadius: "6px",
                    padding: "8px",
                  }}
                >
                  <option value="id">
                    {t("accountingReports.id", {
                      defaultValue: isRTL ? "المعرف" : "ID",
                    })}
                  </option>
                  <option value="date">
                    {t("accountingReports.date", {
                      defaultValue: isRTL ? "التاريخ" : "Date",
                    })}
                  </option>
                  <option value="debit">
                    {t("accountingReports.debit", {
                      defaultValue: isRTL ? "مدين" : "Debit",
                    })}
                  </option>
                  <option value="credit">
                    {t("accountingReports.credit", {
                      defaultValue: isRTL ? "دائن" : "Credit",
                    })}
                  </option>
                  <option value="balance">
                    {t("accountingReports.balance", {
                      defaultValue: isRTL ? "الرصيد" : "Balance",
                    })}
                  </option>
                </select>
              </label>
              <label
                style={{
                  color: "#ccc",
                  fontSize: "13px",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                {t("accountingReports.sortOrder", {
                  defaultValue: isRTL ? "الاتجاه" : "Order",
                })}
                <select
                  value={sortDirection}
                  onChange={e => setSortDirection(e.target.value)}
                  style={{
                    backgroundColor: "#14263b",
                    color: "white",
                    border: "1px solid #333",
                    borderRadius: "6px",
                    padding: "8px",
                  }}
                >
                  <option value="asc">
                    {t("accountingReports.ascending", {
                      defaultValue: isRTL
                        ? "تصاعدي: من الصغير للكبير"
                        : "Ascending: small to large",
                    })}
                  </option>
                  <option value="desc">
                    {t("accountingReports.descending", {
                      defaultValue: isRTL
                        ? "تنازلي: من الكبير للصغير"
                        : "Descending: large to small",
                    })}
                  </option>
                </select>
              </label>
            </div>
            <div
              style={{
                overflowX: "auto",
                backgroundColor: "#111826",
                borderRadius: "8px",
                padding: "12px",
                direction: dir,
              }}
            >
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  color: "#eee",
                  fontSize: "13px",
                  direction: dir,
                }}
              >
                <thead>
                  <tr style={{ backgroundColor: "#252f3f" }}>
                    <th style={{ borderBottom: "1px solid #333", padding: "10px", textAlign: "center", width: "36px" }}>#</th>
                    <th
                      style={{
                        borderBottom: "1px solid #333",
                        padding: "10px",
                        textAlign: "center",
                        cursor: 'pointer',
                      }}
                      onClick={() => handleSort('id')}
                    >
                      {t("accountingReports.id", {
                        defaultValue: isRTL ? "المعرف" : "ID",
                      })}{getSortIcon('id')}
                    </th>
                    <th
                      style={{
                        borderBottom: "1px solid #333",
                        padding: "10px",
                        textAlign,
                        cursor: 'pointer',
                      }}
                      onClick={() => handleSort('date')}
                    >
                      {t("accountingReports.date", {
                        defaultValue: isRTL ? "التاريخ" : "Date",
                      })}{getSortIcon('date')}
                    </th>
                    <th
                      style={{
                        borderBottom: "1px solid #333",
                        padding: "10px",
                        textAlign,
                      }}
                    >
                      {t("accountingReports.description", {
                        defaultValue: isRTL ? "الوصف" : "Description",
                      })}
                    </th>
                    <th
                      style={{
                        borderBottom: "1px solid #333",
                        padding: "10px",
                        textAlign: "center",
                        cursor: 'pointer',
                      }}
                      onClick={() => handleSort('debit')}
                    >
                      {t("accountingReports.debit", {
                        defaultValue: isRTL ? "مدين" : "Debit",
                      })}{getSortIcon('debit')}
                    </th>
                    <th
                      style={{
                        borderBottom: "1px solid #333",
                        padding: "10px",
                        textAlign: "center",
                        cursor: 'pointer',
                      }}
                      onClick={() => handleSort('credit')}
                    >
                      {t("accountingReports.credit", {
                        defaultValue: isRTL ? "دائن" : "Credit",
                      })}{getSortIcon('credit')}
                    </th>
                    <th
                      style={{
                        borderBottom: "1px solid #333",
                        padding: "10px",
                        textAlign: "center",
                        cursor: 'pointer',
                      }}
                      onClick={() => handleSort('balance')}
                    >
                      {t("accountingReports.balance", {
                        defaultValue: isRTL ? "الرصيد" : "Balance",
                      })}{getSortIcon('balance')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedEntries.length === 0 && (
                    <tr>
                      <td
                        colSpan={7}
                        style={{
                          padding: "12px",
                          textAlign: "center",
                          color: "#bbb",
                        }}
                      >
                        {t("accountingReports.noData", {
                          defaultValue: "لا توجد بيانات",
                        })}
                      </td>
                    </tr>
                  )}
                  {sortedEntries.map((row, idx) => {
                    const debit = getEntryDebit(row);
                    const credit = getEntryCredit(row);
                    const balance = getEntryBalance(row);
                    return (
                      <tr
                        key={row.id || row.entry_id || idx}
                        style={{ borderBottom: "1px solid #222" }}
                      >
                        <td style={{ padding: "10px", textAlign: "center" }}>
                          {idx + 1}
                        </td>
                        <td style={{ padding: "10px", textAlign: "center" }}>
                          {getEntryId(row, idx + 1)}
                        </td>
                        <td style={{ padding: "10px", textAlign }}>
                          {formatDate(getEntryDate(row))}
                        </td>
                        <td style={{ padding: "10px", textAlign }}>
                          {getEntryDescription(row) || "—"}
                        </td>
                        <td style={{ padding: "10px", textAlign: "center" }}>
                          {formatNumber(debit)}
                        </td>
                        <td style={{ padding: "10px", textAlign: "center" }}>
                          {formatNumber(credit)}
                        </td>
                        <td
                          style={{
                            padding: "10px",
                            textAlign: "center",
                            fontWeight: "bold",
                          }}
                        >
                          {formatNumber(balance)}
                        </td>
                      </tr>
                    );
                  })}
                  <tr
                    style={{ backgroundColor: "#1f2937", fontWeight: "bold" }}
                  >
                    <td style={{ padding: "10px", textAlign }} colSpan={4}>
                      {t("accountingReports.total", {
                        defaultValue: "الإجمالي",
                      })}
                    </td>
                    <td style={{ padding: "10px", textAlign: "center" }}>
                      {formatNumber(totals.total_debit ?? totals.debit ?? 0)}
                    </td>
                    <td style={{ padding: "10px", textAlign: "center" }}>
                      {formatNumber(totals.total_credit ?? totals.credit ?? 0)}
                    </td>
                    <td style={{ padding: "10px", textAlign: "center" }}>
                      {formatNumber(
                        totals.closing_balance ??
                          totals.total_balance ??
                          totals.balance ??
                          0,
                      )}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* PDF Print Templates (one full page per chunk) */}
      {pdfType === "all" &&
        accountStatementReport &&
        pdfPages &&
        pdfPages.length > 0 &&
        pdfPages.map((page, pageIndex) => (
          <AccountStatementReportPdfPage
            key={pageIndex}
            idPrefix="account-statement-report-pdf-all"
            page={page}
            pageIndex={pageIndex}
            isRTL={isRTL}
            dir={dir}
            t={t}
            publicUrl={publicUrl}
            headerLogo={headerLogo}
            accountId={accountId}
            accountInfo={accountInfo}
            period={period}
            fromDate={fromDate}
            toDate={toDate}
            totals={totals}
            formatNumber={formatNumber}
            formatDate={formatDate}
            hidden
          />
        ))}

      <PdfPreviewModal
        isOpen={isPreviewOpen && !!accountStatementReport}
        title={t("accountingReports.accountStatementTitle", {
          defaultValue: "كشف حساب",
        })}
        onClose={() => setIsPreviewOpen(false)}
        onPrint={handlePrintAll}
        isGeneratingPdf={isGeneratingPdf}
        contentWidth="794px"
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "10px",
          }}
        >
          {pdfPages &&
            pdfPages.length > 0 &&
            pdfPages.map((page, pageIndex) => (
              <AccountStatementReportPdfPage
                key={pageIndex}
                idPrefix="preview-account-statement-report-pdf-all"
                page={page}
                pageIndex={pageIndex}
                isRTL={isRTL}
                dir={dir}
                t={t}
                publicUrl={publicUrl}
                headerLogo={headerLogo}
                accountId={accountId}
                accountInfo={accountInfo}
                period={period}
                fromDate={fromDate}
                toDate={toDate}
                totals={totals}
                formatNumber={formatNumber}
                formatDate={formatDate}
              />
            ))}
        </div>
      </PdfPreviewModal>

      {/* Hidden measurement template used to calculate dynamic PDF pagination */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          left: "-9999px",
          top: 0,
          width: "794px",
          pointerEvents: "none",
          visibility: "hidden",
        }}
      >
        <div
          id="account-statement-report-pdf-measure-top-header"
          style={{ width: "100%" }}
        >
          <img
            src={`${publicUrl}/HeaderAce.png`}
            alt=""
            style={{
              width: "100%",
              height: "auto",
              objectFit: "contain",
              marginBottom: "8px",
              borderBottom: "4px double #000",
              paddingBottom: "8px",
            }}
          />
          <div
            style={{
              marginTop: "12px",
              textAlign: "center",
              marginBottom: "20px",
            }}
          >
            <h2 style={{ fontSize: "20px", fontWeight: "700", margin: 0 }}>
              {t("accountingReports.accountStatementTitle", {
                defaultValue: "كشف حساب",
              })}
            </h2>
            <div style={{ fontSize: "14px", marginTop: "8px" }}>
              {accountInfo
                ? isRTL
                  ? accountInfo.name_ar || accountInfo.name_en || accountInfo.name
                  : accountInfo.name_en || accountInfo.name_ar || accountInfo.name
                : `#${accountId}`}{" "}
              -{" "}
              {period?.from && period?.to
                ? `${formatDate(period.from)} - ${formatDate(period.to)}`
                : `${formatDate(fromDate)} - ${formatDate(toDate)}`}
            </div>
          </div>
          {/* Reserve space for the first-page opening/closing balance summary lines so pagination never overflows into the footer on page 1 */}
          {(totals?.opening_balance !== undefined ||
            totals?.closing_balance !== undefined) && (
            <div style={{ textAlign: "center", marginBottom: "10px" }}>
              {totals?.opening_balance !== undefined && (
                <div style={{ fontSize: "12px", marginTop: "4px" }}>
                  {t("accountingReports.openingBalance", {
                    defaultValue: "الرصيد الافتتاحي",
                  })}
                  : 0.00
                </div>
              )}
              {totals?.closing_balance !== undefined && (
                <div style={{ fontSize: "12px", marginTop: "4px" }}>
                  {t("accountingReports.closingBalance", {
                    defaultValue: "الرصيد الختامي",
                  })}
                  : 0.00
                </div>
              )}
            </div>
          )}
        </div>
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            fontSize: "12px",
            border: "1px solid #000",
          }}
        >
          <thead id="account-statement-report-pdf-measure-thead">
            <tr>
              <th style={{ border: "1px solid #000", padding: "6px" }}>#</th>
              <th style={{ border: "1px solid #000", padding: "6px" }}>
                {t("accountingReports.date", { defaultValue: "التاريخ" })}
              </th>
              <th style={{ border: "1px solid #000", padding: "6px" }}>
                {t("accountingReports.description", {
                  defaultValue: "الوصف",
                })}
              </th>
              <th style={{ border: "1px solid #000", padding: "6px" }}>
                {t("accountingReports.debit", { defaultValue: "مدين" })}
              </th>
              <th style={{ border: "1px solid #000", padding: "6px" }}>
                {t("accountingReports.credit", { defaultValue: "دائن" })}
              </th>
              <th style={{ border: "1px solid #000", padding: "6px" }}>
                {t("accountingReports.balance", { defaultValue: "الرصيد" })}
              </th>
            </tr>
          </thead>
          <tbody id="account-statement-report-pdf-measure-tbody">
            {sortedEntries.slice(0, 5).map((row, idx) => (
              <tr key={idx}>
                <td style={{ border: "1px solid #000", padding: "6px" }}>
                  {idx + 1}
                </td>
                <td style={{ border: "1px solid #000", padding: "6px" }}>
                  {formatDate(getEntryDate(row)) || "—"}
                </td>
                <td style={{ border: "1px solid #000", padding: "6px" }}>
                  {getEntryDescription(row) || "—"}
                </td>
                <td style={{ border: "1px solid #000", padding: "6px" }}>
                  {formatNumber(getEntryDebit(row))}
                </td>
                <td style={{ border: "1px solid #000", padding: "6px" }}>
                  {formatNumber(getEntryCredit(row))}
                </td>
                <td style={{ border: "1px solid #000", padding: "6px" }}>
                  {formatNumber(getEntryBalance(row))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div
          id="account-statement-report-pdf-measure-footer"
          style={{ height: "120px" }}
        />
      </div>
    </div>
  );
};

const AccountStatementReportPdfPage = ({
  idPrefix,
  page,
  pageIndex,
  isRTL,
  dir,
  t,
  publicUrl,
  headerLogo,
  accountId,
  accountInfo,
  period,
  fromDate,
  toDate,
  totals,
  formatNumber,
  formatDate,
  hidden = false,
}) => {
  const entries = page.entries || [];
  return (
    <div
      id={`${idPrefix}-${pageIndex}`}
      aria-hidden={hidden || undefined}
      style={{
        position: hidden ? "absolute" : "static",
        left: hidden ? "-9999px" : undefined,
        top: hidden ? 0 : undefined,
        width: "794px",
        minHeight: "1123px",
        height: "auto",
        backgroundColor: "#ffffff",
        color: "#000000",
        fontFamily:
          '"Times New Roman Bold", "Times New Roman", "Times", serif',
        fontWeight: "bold",
        padding: "40px",
        boxSizing: "border-box",
        overflow: "visible",
        boxShadow: hidden ? undefined : "0 4px 20px rgba(0, 0, 0, 0.5)",
      }}
    >
      <div
        style={{
          border: "5px solid #000",
          padding: "3px",
          backgroundColor: "#ffffff",
          boxSizing: "border-box",
          width: "100%",
          minHeight: "1043px",
          height: "auto",
          overflow: "visible",
        }}
      >
        <div
          style={{
            border: "2px solid #000",
            minHeight: "1033px",
            height: "1033px",
            padding: "18px 18px 10px",
            position: "relative",
            display: "flex",
            flexDirection: "column",
            fontFamily:
              '"Times New Roman Bold", "Times New Roman", "Times", serif',
            fontWeight: "bold",
            WebkitFontSmoothing: "antialiased",
            MozOsxFontSmoothing: "grayscale",
            textRendering: "optimizeLegibility",
            direction: isRTL ? "rtl" : "ltr",
            backgroundColor: "#ffffff",
            boxSizing: "border-box",
            width: "100%",
            overflow: "visible",
          }}
        >
          <div
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: "100%",
              height: "600px",
              backgroundImage: `url(${headerLogo})`,
              backgroundSize: "95%",
              backgroundPosition: "center center",
              backgroundRepeat: "no-repeat",
              opacity: 0.1,
              zIndex: 0,
              pointerEvents: "none",
            }}
          />
          <div
            style={{
              position: "relative",
              zIndex: 1,
              display: "flex",
              flexDirection: "column",
              height: "100%",
              minHeight: "100%",
            }}
          >
            <img
              src={`${publicUrl}/HeaderAce.png`}
              alt="Header"
              style={{
                width: "100%",
                height: "auto",
                objectFit: "contain",
                marginBottom: "8px",
                borderBottom: "4px double #000",
                paddingBottom: "8px",
                flexShrink: 0,
              }}
              crossOrigin="anonymous"
            />

            <div
              style={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                overflow: "visible",
                minHeight: 0,
                paddingBottom: "120px",
              }}
            >
              <div
                style={{
                  marginTop: "12px",
                  textAlign: "center",
                  marginBottom: "20px",
                  direction: dir,
                }}
              >
                <h2
                  style={{
                    fontSize: "20px",
                    fontWeight: "700",
                    color: "#000080",
                    margin: 0,
                    direction: dir,
                  }}
                >
                  {t("accountingReports.accountStatementTitle", {
                    defaultValue: "كشف حساب",
                  })}
                </h2>
                <div
                  style={{
                    fontSize: "14px",
                    marginTop: "8px",
                    direction: dir,
                  }}
                >
                  {accountInfo
                    ? isRTL
                      ? accountInfo.name_ar ||
                        accountInfo.name_en ||
                        accountInfo.name
                      : accountInfo.name_en ||
                        accountInfo.name_ar ||
                        accountInfo.name
                    : `#${accountId}`}{" "}
                  -{" "}
                  {period?.from && period?.to
                    ? `${formatDate(period.from)} - ${formatDate(period.to)}`
                    : `${formatDate(fromDate)} - ${formatDate(toDate)}`}
                </div>
                {page.isFirstPage && totals?.opening_balance !== undefined && (
                  <div style={{ fontSize: "12px", marginTop: "4px" }}>
                    {t("accountingReports.openingBalance", {
                      defaultValue: "الرصيد الافتتاحي",
                    })}
                    : {formatNumber(totals.opening_balance)}
                  </div>
                )}
                {page.isFirstPage && totals?.closing_balance !== undefined && (
                  <div style={{ fontSize: "12px", marginTop: "4px" }}>
                    {t("accountingReports.closingBalance", {
                      defaultValue: "الرصيد الختامي",
                    })}
                    : {formatNumber(totals.closing_balance)}
                  </div>
                )}
              </div>
              <div
                style={{
                  color: "#000",
                  fontSize: "13px",
                  marginTop: "10px",
                }}
              >
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    fontSize: "12px",
                    direction: isRTL ? "rtl" : "ltr",
                  }}
                >
                  <thead>
                    <tr>
                      <th
                        style={{
                          border: "1px solid #000",
                          padding: "6px",
                          backgroundColor: "#e5e7eb",
                          textAlign: "center",
                        }}
                      >
                        #
                      </th>
                      <th
                        style={{
                          border: "1px solid #000",
                          padding: "6px",
                          backgroundColor: "#e5e7eb",
                          textAlign: isRTL ? "right" : "left",
                        }}
                      >
                        {t("accountingReports.date", {
                          defaultValue: "التاريخ",
                        })}
                      </th>
                      <th
                        style={{
                          border: "1px solid #000",
                          padding: "6px",
                          backgroundColor: "#e5e7eb",
                          textAlign: isRTL ? "right" : "left",
                        }}
                      >
                        {t("accountingReports.description", {
                          defaultValue: "الوصف",
                        })}
                      </th>
                      <th
                        style={{
                          border: "1px solid #000",
                          padding: "6px",
                          backgroundColor: "#e5e7eb",
                          textAlign: "center",
                        }}
                      >
                        {t("accountingReports.debit", {
                          defaultValue: "مدين",
                        })}
                      </th>
                      <th
                        style={{
                          border: "1px solid #000",
                          padding: "6px",
                          backgroundColor: "#e5e7eb",
                          textAlign: "center",
                        }}
                      >
                        {t("accountingReports.credit", {
                          defaultValue: "دائن",
                        })}
                      </th>
                      <th
                        style={{
                          border: "1px solid #000",
                          padding: "6px",
                          backgroundColor: "#e5e7eb",
                          textAlign: "center",
                        }}
                      >
                        {t("accountingReports.balance", {
                          defaultValue: "الرصيد",
                        })}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {entries.length === 0 && (
                      <tr>
                        <td
                          colSpan={6}
                          style={{
                            border: "1px solid #000",
                            padding: "8px",
                            textAlign: "center",
                          }}
                        >
                          {t("accountingReports.noData", {
                            defaultValue: "لا توجد بيانات",
                          })}
                        </td>
                      </tr>
                    )}
                    {entries.map((row, idx) => {
                      const debit = getEntryDebit(row);
                      const credit = getEntryCredit(row);
                      const balance = getEntryBalance(row);
                      return (
                        <tr key={row.id || row.entry_id || idx}>
                          <td
                            style={{
                              border: "1px solid #000",
                              padding: "6px",
                              textAlign: "center",
                            }}
                          >
                            {page.startIndex + idx + 1}
                          </td>
                          <td
                            style={{
                              border: "1px solid #000",
                              padding: "6px",
                              textAlign: isRTL ? "right" : "left",
                            }}
                          >
                            {formatDate(getEntryDate(row))}
                          </td>
                          <td
                            style={{
                              border: "1px solid #000",
                              padding: "6px",
                              textAlign: isRTL ? "right" : "left",
                            }}
                          >
                            {getEntryDescription(row) || "—"}
                          </td>
                          <td
                            style={{
                              border: "1px solid #000",
                              padding: "6px",
                              textAlign: "center",
                            }}
                          >
                            {formatNumber(debit)}
                          </td>
                          <td
                            style={{
                              border: "1px solid #000",
                              padding: "6px",
                              textAlign: "center",
                            }}
                          >
                            {formatNumber(credit)}
                          </td>
                          <td
                            style={{
                              border: "1px solid #000",
                              padding: "6px",
                              textAlign: "center",
                              fontWeight: "bold",
                            }}
                          >
                            {formatNumber(balance)}
                          </td>
                        </tr>
                      );
                    })}
                    {page.isLastPage && (
                      <tr
                        style={{
                          backgroundColor: "#f5f5f5",
                          fontWeight: "bold",
                        }}
                      >
                        <td
                          style={{
                            border: "1px solid #000",
                            padding: "8px",
                            textAlign: isRTL ? "right" : "left",
                          }}
                          colSpan={3}
                        >
                          {t("accountingReports.total", {
                            defaultValue: "الإجمالي",
                          })}
                        </td>
                        <td
                          style={{
                            border: "1px solid #000",
                            padding: "8px",
                            textAlign: "center",
                          }}
                        >
                          {formatNumber(
                            totals.total_debit ?? totals.debit ?? 0,
                          )}
                        </td>
                        <td
                          style={{
                            border: "1px solid #000",
                            padding: "8px",
                            textAlign: "center",
                          }}
                        >
                          {formatNumber(
                            totals.total_credit ?? totals.credit ?? 0,
                          )}
                        </td>
                        <td
                          style={{
                            border: "1px solid #000",
                            padding: "8px",
                            textAlign: "center",
                          }}
                        >
                          {formatNumber(
                            totals.closing_balance ??
                              totals.total_balance ??
                              totals.balance ??
                              0,
                          )}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div
            style={{
              position: "absolute",
              bottom: "10px",
              left: "18px",
              right: "18px",
              flexShrink: 0,
            }}
          >
            <img
              src={`${publicUrl}/FooterAce.png`}
              alt="Footer"
              style={{
                width: "100%",
                height: "auto",
                objectFit: "contain",
              }}
              crossOrigin="anonymous"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountStatementReport;

